import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import "./PostContent.scss";
import LinkPreview from "../Components/LinkPreview/LinkPreview";
import { extractFirstLink } from "../helpers/helper";
import { useNavigate } from "react-router-dom";

const PostContent = ({ content, gif }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowMore, setShouldShowMore] = useState(false);
  const contentRef = useRef(null);
  const [url, setUrl] = useState(undefined);
  const [mediaModal, setMediaModal] = useState({ open: false, media: null, type: null });
  const navigate = useNavigate();

  // Function to check content height
  const checkContentHeight = useCallback(() => {
    if (contentRef.current && !isExpanded) {
      // Only check height when content is NOT expanded
      const contentHeight = contentRef.current.scrollHeight;
      const clientHeight = contentRef.current.clientHeight;
      
      const hasHiddenContent = contentHeight > clientHeight + 10; // 10px buffer for precision
      
      // Additional check: ensure content is actually being clipped at reasonable height
      const isContentClipped = clientHeight < 300 && contentHeight > clientHeight * 1.2;
      
      // Show "Show more" if there's hidden content or content is being clipped
      const shouldShow = hasHiddenContent || isContentClipped;
      
      setShouldShowMore(shouldShow);
    } else if (isExpanded) {
      // When expanded, always show the button so user can collapse
      setShouldShowMore(true);
    }
  }, [isExpanded]);

  useEffect(() => {
    // Set URL first
    setUrl(extractFirstLink(content));
    
    // Check content height with multiple timing approaches
    const checkHeights = () => {
      checkContentHeight();
      // Double check after a brief delay for any late-rendering content
      setTimeout(checkContentHeight, 100);
      setTimeout(checkContentHeight, 500);
    };
    
    // Initial check
    checkHeights();

    // Also check when images or other resources load
    window.addEventListener('load', checkContentHeight);
    window.addEventListener('resize', checkContentHeight);

    return () => {
      window.removeEventListener('load', checkContentHeight);
      window.removeEventListener('resize', checkContentHeight);
    };
  }, [content, checkContentHeight]);

  // Add ResizeObserver to detect when content size changes
  useEffect(() => {
    if (!contentRef.current) return;
    
    const resizeObserver = new ResizeObserver(() => {
      // Check height when the element itself changes size
      setTimeout(checkContentHeight, 50);
    });
    
    resizeObserver.observe(contentRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [checkContentHeight]);

  // Notify parent components when expansion state changes
  useEffect(() => {
    // Dispatch a custom event when expansion state changes
    // This allows parent components to adjust their layout accordingly
    window.dispatchEvent(new CustomEvent('post-content-expanded', {
      detail: { expanded: isExpanded }
    }));
  }, [isExpanded]);

  const toggleExpansion = (e) => {
    e.stopPropagation(); // Prevent click from propagating to parent elements
    setIsExpanded(!isExpanded);

    // If expanding, scroll to ensure the content is visible
    if (!isExpanded) {
      setTimeout(() => {
        contentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  };

  const handleGifClick = (e) => {
    e.stopPropagation();
    setMediaModal({ open: true, media: gif, type: 'gif' });
  };

const processContent = (rawContent) => {
  // Remove all leading empty lines and whitespace at the start
  rawContent = rawContent.replace(/^[\s\r\n]+/, '');
  rawContent = rawContent.replace(/[\uFEFF\u200B\u00A0]/g, '').replace(/\r\n/g, '\n').replace(/\n/g, '  \n')

  // Normalize all newlines to \n
  const normalized = rawContent.replace(/\r\n|\r/g, '\n');

  // Split into lines and process each line
  const lines = normalized.split('\n');
  let processedLines = [];
  const markdownLinkRegex = /\[[^\]]+\]\([^)]+\)/;
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    if (markdownLinkRegex.test(line)) {
      // Extract markdown links and replace with placeholders
      line = line.replace(/\[[^\]]+\]\([^)]+\)/g, (match) => {
        return match;
      });

      // Apply formatting to the rest of the line
      line = line.replace(/__([^_]+)__/g, '<span class="underline">$1</span>');
      line = line.replace(/~~([^~]+)~~/g, '<span class="strikethrough">$1</span>');
      line = line.replace(/\*\*([^*]+)\*\*/g, '<span class="bold">$1</span>');
      line = line.replace(/_([^_]+)_/g, '<span class="italic">$1</span>');
      processedLines.push(line);
    } else if (/^\s*([-*+]|\d+\.)\s/.test(line) || /^#/.test(line)) {
      // Apply formatting to list/header lines
      line = line.replace(/__([^_]+)__/g, '<span class="underline">$1</span>');
      line = line.replace(/~~([^~]+)~~/g, '<span class="strikethrough">$1</span>');
      line = line.replace(/\*\*([^*]+)\*\*/g, '<span class="bold">$1</span>');
      line = line.replace(/_([^_]+)_/g, '<span class="italic">$1</span>');
      processedLines.push(line);
    } else if (line.trim() === '') {
      processedLines.push('<br>');
    } else {
      // Apply formatting to normal lines
      line = line.replace(/__([^_]+)__/g, '<span class="underline">$1</span>');
      line = line.replace(/~~([^~]+)~~/g, '<span class="strikethrough">$1</span>');
      line = line.replace(/\*\*([^*]+)\*\*/g, '<span class="bold">$1</span>');
      line = line.replace(/_([^_]+)_/g, '<span class="italic">$1</span>');
      processedLines.push(line + '<br>');
    }
  }
  let processed = processedLines.join('\n');
  return processed;
};

  if (!content) return null;

  return (
    <div className={`post-content-wrapper ${shouldShowMore ? 'has-more-content' : ''}`}>
      <div
        className={`post-content ${isExpanded ? 'expanded' : ''}`}
        ref={contentRef}
        onClick={shouldShowMore ? toggleExpansion : undefined}
      >
        <ReactMarkdown
          children={processContent(content)}
          components={{
            span: ({ node, className, children }) => {
              if (className === 'underline') {
                return <span style={{ textDecoration: 'underline' }}>{children}</span>;
              }
              if (className === 'strikethrough') {
                return <span style={{ textDecoration: 'line-through' }}>{children}</span>;
              }
              if (className === 'bold') {
                return <span style={{ fontWeight: 'bold' }}>{children}</span>;
              }
              if (className === 'italic') {
                return <span style={{ fontStyle: 'italic' }}>{children}</span>;
              }
              return <span>{children}</span>;
            },
            a: ({ node, ...props }) => {
              if (
                  typeof props.href === 'string' &&
                  props.href.startsWith('/community/profiles/')
              ) {
                let username = props.children;
                if (typeof username === 'string' && username.startsWith('@')) {
                  username = username.slice(1);
                }
                return <span className="mentioned_user" onClick={()=>{
                  navigate(props.href);
                }}>{username}</span>;
              }
              return (
                  <a {...props} target="_blank" rel="noopener noreferrer">
                    {props.children}
                  </a>
              );
            }

          }}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        />
        {url && <LinkPreview url={url} />}
        {gif && (
          <div className="post-gif">
            <img
              src={gif}
              alt="GIF"
              style={{ maxWidth: "220px", maxHeight: "180px", borderRadius: 8, padding: "6px", display: "block", cursor: "pointer" }}
              onClick={handleGifClick}
            />
          </div>
        )}
      </div>
      {shouldShowMore && (
        <div
          className={`show-more-content ${isExpanded ? 'expanded' : ''}`}
          onClick={toggleExpansion}
        >
          {isExpanded ? "Show less" : "Show more"}
        </div>
      )}
      {/* Media Modal for full screen GIF view */}
      {mediaModal.open && (
        <div className="media-overlay" onClick={() => setMediaModal({ open: false, media: null, type: null })}>
          <div className="overlay-background"></div>
          <div className="expanded-media-container" onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
            <button
              className="close-button"
              style={{
                position: 'fixed', top: 24, right: 32, zIndex: 1001, fontSize: 36,
                background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', cursor: 'pointer',
                borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              onClick={() => setMediaModal({ open: false, media: null, type: null })}
              aria-label="Close fullscreen media"
            >
              ×
            </button>
            <div className="expanded-media-wrapper">
              {mediaModal.type === 'gif' && (
                <img src={mediaModal.media} alt="" className="expanded-media" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostContent;